import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowRight, CheckCircle, Hospital, Home, Layers } from 'lucide-react';
import Seo from '../components/Seo';

const flowStages = [
  {
    label: 'Demand Intelligence',
    title: 'Intake + Surge Forecasting',
    description:
      'Triage, referrals, elective schedules, and external partners all feed inconsistent demand signals that still must be reconciled with capacity.',
  },
  {
    label: 'Coordination Hub',
    title: 'Patient Flow Queueing',
    description:
      'Disparate teams fight over beds, OR slots, and staffing while supply deliveries and lab results trickle in at different cadences.',
  },
  {
    label: 'Resource Alignment',
    title: 'Staff, Equipment & Logistics',
    description:
      'Nurses, therapists, transportation, PPE, and remote monitoring gear are choreographed by spreadsheets and crisis triage calls.',
  },
  {
    label: 'Transition Management',
    title: 'Discharge & Postacute Handoff',
    description:
      'Multiple checklists, prior authorizations, transport requests, and home health referrals must align before a patient can move.',
  },
];

const hospitalFlow = [
  {
    step: 'Emergency Demand',
    detail: 'Ride-out spikes with flexible capacity models while keeping beds, staffing, and diagnostics in sync.',
  },
  {
    step: 'Clinical Unit Assignment',
    detail: 'Match acuity, specialty, and staffing, while ensuring supplies and transport teams are ready for the move.',
  },
  {
    step: 'Surgical & Procedural Flow',
    detail: 'Balance stacks of cases, anesthesia teams, recovery rooms, and sterilization without breaking the schedule.',
  },
  {
    step: 'Discharge & Readiness',
    detail: 'Orchestrate rehab, home health, outpatient, and pharmacy transitions before the bed can be booked again.',
  },
];

const homeHealthFlow = [
  {
    step: 'Referral Intake',
    detail: 'Capture discharge instructions, payer rules, and specialist notes that often arrive as fragmented documents.',
  },
  {
    step: 'Visit Routing',
    detail: 'Match clinicians to patients with the right skill mix, travel time, and supply kits without manual spreadsheets.',
  },
  {
    step: 'In-Home Execution',
    detail: 'Coordinate telehealth, remote monitoring feeds, and supply drops while updating the originating hospital team.',
  },
  {
    step: 'Clinical Feedback Loop',
    detail: 'Close the loop with status reports, urgent flags, and documentation exported back into hospital workflows.',
  },
];

const deltaOpsImpact = [
  {
    title: 'Unified Signal Layer',
    description:
      'DeltaOps ingests messaging, orders, staffing notes, and shipment ETAs so leaders see the full supply chain picture at a glance.',
  },
  {
    title: 'AI Coordination Agents',
    description:
      'Autonomous workflows nudge teams when constraints change, suggest staffing adjustments, and flag bottlenecks before they cascade.',
  },
  {
    title: 'Hospital + Home Health Continuity',
    description:
      'One platform keeps acute, peri-acute, and home health teams synchronized, reducing double entry and preventing handoff noise.',
  },
];

const OperationsFlowPage = () => (
  <div className="bg-slate-950 text-white">
    <Seo
      title="Operations Flow | DeltaOps AI Coordination Layer"
      description="Explore the end-to-end hospital and home health supply chain, from demand intelligence to discharge and post-acute handoffs."
      image="https://images.pexels.com/photos/7108145/pexels-photo-7108145.jpeg"
      imageAlt="Clinicians coordinating care with digital tools"
    />
    <section
      className="min-h-screen flex items-center bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900/20"
      data-testid="operations-flow-hero"
    >
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-5xl space-y-6">
          <p className="text-cyan-300 uppercase tracking-widest text-sm">Operations Intelligence</p>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight">
            Healthcare Delivery Functions as a Complex Supply Chain - Coordination Is the Bottleneck
          </h1>
          <p className="text-lg text-slate-200">
            Every admission, transfer, discharge, and in-home visit is part of the healthcare delivery supply chain. Patients, clinicians, medications, equipment, and information must move seamlessly across units, facilities, and care settings. However, much of the care coordination still relies on manual calls, fragmented systems, and reactive decision-making. This page maps the complex flows in hospitals and home health, highlights where coordination breaks down, and shows how DeltaOps provides the orchestration layer that connects each link in the healthcare delivery chain.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="btn bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg transition hover:-translate-y-0.5"
            >
              Request Operations Review
            </Link>
            <Link
              to="/success-stories"
              className="btn border border-white/20 px-6 py-3 rounded-lg text-white hover:text-cyan-300"
            >
              See the DeltaOps Advantage
            </Link>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 bg-slate-900">
      <div className="container mx-auto px-4 space-y-8">
        <div>
          <p className="text-cyan-300 uppercase tracking-widest text-sm mb-2">End-to-end Supply Chain</p>
          <h2 className="text-3xl font-bold">Flow Diagram: From Demand to Transition</h2>
          <p className="text-slate-200 max-w-3xl">
            This diagram condenses the ongoing pulse of hospital operations and home health logistics. Each stage
            connects demand, staffing, equipment, and handoffs.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-6 text-left">
          {flowStages.map((stage, index) => (
            <div key={stage.label} className="flex items-start max-w-xs gap-4">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/30 border border-cyan-500 flex items-center justify-center text-xl font-bold">
                  {index + 1}
                </div>
                {index < flowStages.length - 1 && (
                  <div className="flex-1 border-l border-dashed border-cyan-500/60 mx-auto h-10" />
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-cyan-300">{stage.label}</p>
                <h3 className="text-xl font-semibold text-white">{stage.title}</h3>
                <p className="text-slate-300 text-sm">{stage.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-950">
      <div className="container mx-auto px-4 space-y-12">
        <div className="grid gap-6 md:grid-cols-2">
          {[
            { title: 'Hospital Operations', icon: <Hospital className="w-6 h-6" />, flow: hospitalFlow },
            { title: 'Home Health Operations', icon: <Home className="w-6 h-6" />, flow: homeHealthFlow },
          ].map((track) => (
            <div
              key={track.title}
              className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-6 backdrop-blur"
            >
              <div className="flex items-center gap-3 text-cyan-300">
                {track.icon}
                <p className="text-sm uppercase tracking-widest text-cyan-300">{track.title}</p>
              </div>
              <div className="space-y-4">
                {track.flow.map((item) => (
                  <div key={item.step} className="flex gap-3 items-start">
                    <div className="mt-1">
                      <ArrowRight className="text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">{item.step}</h4>
                      <p className="text-slate-300 text-sm">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="bg-slate-900/80 border border-white/10 rounded-3xl p-8 space-y-4">
          <div className="text-sm uppercase tracking-widest text-cyan-200 flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Operations Layers
          </div>
          <h3 className="text-3xl font-bold text-white">DeltaOps orchestrates every handoff</h3>
          <p className="text-slate-300 max-w-3xl">
            DeltaOps transforms these flows from manual handoffs into a living, observable supply chain. Real-time
            intelligence, plan-aware AI agents, and one-click communication loops keep every team aligned.
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            {deltaOpsImpact.map((impact) => (
              <div
                key={impact.title}
                className="bg-slate-900 rounded-2xl border border-white/10 p-4 space-y-2 hover:border-cyan-400/60 transition"
              >
                <div className="text-cyan-400">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <h4 className="text-lg font-semibold">{impact.title}</h4>
                <p className="text-slate-300 text-sm">{impact.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 bg-gradient-to-br from-cyan-600/20 via-slate-900 to-slate-950">
      <div className="container mx-auto px-4 text-center">
        <h3 className="text-3xl font-bold text-white">Ready to untangle your healthcare supply chains?</h3>
        <p className="text-slate-200 mb-8">
          DeltaOps brings hospital, post-acute, and home health teams together on one coordination layer so that no
          supply, caregiver, or patient request falls through the cracks.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 font-semibold px-8 py-3 shadow-lg hover:shadow-2xl transition"
        >
          Talk to DeltaOps <ArrowRight className="inline-block" />
        </Link>
      </div>
    </section>
  </div>
);

export default OperationsFlowPage;
